import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const formatEur = (value) => {
    if (value == null) return "-";
    const n = Number(value);
    if (Number.isNaN(n)) return "-";
    return `${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })} €`;
};

const formatDate = (iso) => {
    if (!iso) return "-";
    try {
        return new Date(iso).toLocaleDateString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        });
    } catch {
        return "-";
    }
};

const StoricoSimulazioni = ({ onOpen, refreshTick }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errore, setErrore] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setErrore("");
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_URL}/ai-agents/simulazioni`, {
                params: { search: debouncedSearch, limit: 100, offset: 0 },
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(res.data.data || []);
        } catch (e) {
            setErrore(e.response?.data?.error || "Errore caricamento storico");
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        fetchData();
    }, [fetchData, refreshTick]);

    const confirmDelete = async () => {
        if (!recordToDelete) return;
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_URL}/ai-agents/simulazioni/${recordToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRecordToDelete(null);
            fetchData();
        } catch (e) {
            setErrore(e.response?.data?.error || "Errore cancellazione");
            setRecordToDelete(null);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="storico-wrapper">
            <div className="storico-toolbar">
                <input
                    type="text"
                    className="storico-search"
                    placeholder="Cerca per nome o cognome..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <span className="storico-count">
                    {loading ? "Caricamento..." : `${data.length} simulazion${data.length === 1 ? "e" : "i"}`}
                </span>
            </div>

            {errore && <div className="upload-message error">{errore}</div>}

            <div className="storico-table-wrapper">
                <table className="storico-table">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Cognome</th>
                            <th>CCNL</th>
                            <th>Tipologia</th>
                            <th>Metodo</th>
                            <th>RAL</th>
                            <th>Costo azienda</th>
                            <th>Costo orario</th>
                            <th>Creato il</th>
                            <th>Creato da</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 && !loading && (
                            <tr>
                                <td colSpan={11} className="storico-empty">
                                    Nessuna simulazione presente.
                                </td>
                            </tr>
                        )}
                        {data.map(row => (
                            <tr key={row.id}>
                                <td data-label="Nome">{row.nome_dipendente}</td>
                                <td data-label="Cognome">{row.cognome_dipendente || "-"}</td>
                                <td data-label="CCNL">{row.ccnl}</td>
                                <td data-label="Tipologia">{row.tipologia_contrattuale || "-"}</td>
                                <td data-label="Metodo">
                                    {row.metodo_calcolo === "regole"
                                        ? <span className="badge-metodo badge-regole">Regole</span>
                                        : <span className="badge-metodo badge-llm">Stima AI</span>}
                                </td>
                                <td data-label="RAL" className="storico-num">{formatEur(row.ral)}</td>
                                <td data-label="Costo azienda" className="ai-importo storico-num">
                                    {formatEur(row.costo_azienda_annuo)}
                                </td>
                                <td data-label="Costo orario" className="storico-num">{formatEur(row.costo_orario)}</td>
                                <td data-label="Creato il">{formatDate(row.created_at)}</td>
                                <td data-label="Creato da">{row.created_by_username || "-"}</td>
                                <td data-label="Azioni" className="storico-azioni">
                                    <button className="btn-mini btn-mini-primary" onClick={() => onOpen(row)}>
                                        Apri
                                    </button>
                                    <button className="btn-mini btn-mini-danger" onClick={() => setRecordToDelete(row)}>
                                        Elimina
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {recordToDelete && (
                <div
                    className="conferma-modal-overlay"
                    onClick={() => !deleting && setRecordToDelete(null)}
                >
                    <div className="conferma-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="conferma-modal-header">
                            <div className="conferma-modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    <line x1="12" y1="9" x2="12" y2="13" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                            </div>
                            <h3 className="conferma-modal-title">Elimina simulazione</h3>
                        </div>

                        <div className="conferma-modal-body">
                            <p>
                                Stai per eliminare la simulazione di{" "}
                                <strong>{recordToDelete.nome_dipendente} {recordToDelete.cognome_dipendente || ""}</strong>.
                            </p>
                            <p className="conferma-modal-warning">
                                L'azione non è reversibile.
                            </p>
                        </div>

                        <div className="conferma-modal-actions">
                            <button
                                className="btn-mini"
                                onClick={() => setRecordToDelete(null)}
                                disabled={deleting}
                            >
                                Annulla
                            </button>
                            <button
                                className="btn-mini btn-mini-danger-filled"
                                onClick={confirmDelete}
                                disabled={deleting}
                            >
                                {deleting ? "Eliminazione..." : "Elimina"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoricoSimulazioni;
